// How the text looks (SuiteEditor's EditorStyler, ScreenplayStyling and DividerLayoutFragment), computed for
// CodeMirror's viewport only — the visible lines and a margin — never the whole selection, so a keystroke's styling
// cost doesn't grow with the selection's length.
//
// Prose: emphasis, small caps, underline, the writer's highlights and text colours as marks; inline syntax hidden
// (replaced, and atomic so the caret skips it) unless the marks are shown; book indents; blank lines collapsed; scene
// breaks drawn as ✦; quotations, epigraphs, fences, lists and footnotes as line styles. The block state a line depends
// on (fences, lists, the indent rule) comes from the lines above it in the same fragment: scanned from the fragment's
// start once and cached at the viewport's first line, the cache mapped through edits below it and dropped by an edit
// above it. Screenplays: each line classified in context (Fountain) and set at its element's columns.
import { RangeSet, RangeSetBuilder, StateField, type Range, type Text } from '@codemirror/state';
import {
  Decoration, EditorView, ViewPlugin, WidgetType, type DecorationSet, type ViewUpdate,
} from '@codemirror/view';
import type { ClassifiedElement } from '@writing-suite/core';
import { fountainContentRange, fountainInlineSpans, isSongLine } from '@writing-suite/core';
import { Layout } from './layout.ts';
import { LineScanner, type Span } from './markdown.ts';
import { classifyRange, screenplayRange } from './screenplay.ts';
import { isStage, layoutField, pendingElementField, piecesField, setLayout, setPieces, settingsField } from './state.ts';

// MARK: dividers and chapter openers

class DividerWidget extends WidgetType {
  /** `opens`: a chapter's opening (the large title with room around it); its eyebrow is null for an unnumbered chapter. */
  constructor(readonly title: string, readonly eyebrow: string | null, readonly openerTitle: string | null, readonly first: boolean,
    readonly opens: boolean = eyebrow !== null) { super(); }
  eq(o: DividerWidget) {
    return o.title === this.title && o.eyebrow === this.eyebrow && o.openerTitle === this.openerTitle && o.first === this.first && o.opens === this.opens;
  }
  toDOM() {
    const el = document.createElement('div');
    el.className = 'ws-divider' + (this.opens ? ' ws-opener' : '') + (this.first ? ' ws-divider-first' : '');
    el.setAttribute('aria-hidden', 'true');
    if (this.opens) {
      if (this.eyebrow) {
        const eyebrow = el.appendChild(document.createElement('div'));
        eyebrow.className = 'ws-opener-eyebrow';
        eyebrow.textContent = this.eyebrow;
      }
      const title = el.appendChild(document.createElement('div'));
      title.className = 'ws-opener-title';
      title.textContent = this.openerTitle || this.title || 'Untitled';
      // The scene's own title, quietly, when the chapter's title isn't it.
      if (this.openerTitle && this.title && this.title !== this.openerTitle) {
        const scene = el.appendChild(document.createElement('div'));
        scene.className = 'ws-divider-title ws-opener-scene';
        scene.textContent = this.title;
      }
    } else {
      const title = el.appendChild(document.createElement('span'));
      title.className = 'ws-divider-title';
      title.textContent = this.title || 'Untitled';
    }
    return el;
  }
  // Heights as the book theme draws them (font size 19: 2.6 and 7.2 lines of it), so the height map is right before
  // a divider is drawn.
  get estimatedHeight() { return this.opens ? 137 : 49; }
  ignoreEvent() { return false; }
}

function dividerSet(layout: Layout): DecorationSet {
  const b = new RangeSetBuilder<Decoration>();
  for (let i = 0; i < layout.count; i++) {
    const p = layout.pieces[i];
    const widget = new DividerWidget(p.title, p.opener?.label ?? null, p.opener?.title ?? null, i === 0, !!p.opener);
    b.add(layout.start(i), layout.start(i) + 1, Decoration.replace({ widget, block: true }));
  }
  return b.finish();
}

/** The dividers: block widgets over each `U+FFFC`, rebuilt when the fragments or their titles change, else mapped. */
export const dividers = StateField.define<DecorationSet>({
  create: (state) => dividerSet(state.field(layoutField)),
  update(set, tr) {
    for (const e of tr.effects) if (e.is(setLayout) || e.is(setPieces)) return dividerSet(tr.state.field(layoutField));
    return set.map(tr.changes);
  },
  provide: (f) => EditorView.decorations.from(f),
});

/** The separators between bodies (`\n U+FFFC \n`) and the run's head and tail, atomic so the caret jumps them. */
let atomsCache: { layout: Layout | null; length: number; set: RangeSet<Decoration> } = { layout: null, length: 0, set: RangeSet.empty };
export const structureAtoms = EditorView.atomicRanges.of((view) => {
  const layout = view.state.field(layoutField);
  if (atomsCache.layout !== layout || atomsCache.length !== view.state.doc.length) {
    const b = new RangeSetBuilder<Decoration>();
    const mark = Decoration.mark({});
    for (let i = 0; i < layout.count; i++) {
      const from = i === 0 ? 0 : layout.to[i - 1];
      if (layout.from[i] > from) b.add(from, layout.from[i], mark);
    }
    if (layout.count && view.state.doc.length > layout.to[layout.count - 1]) b.add(layout.to[layout.count - 1], view.state.doc.length, mark);
    atomsCache = { layout, length: view.state.doc.length, set: b.finish() };
  }
  return atomsCache.set;
});

// MARK: prose

class OrnamentWidget extends WidgetType {
  eq() { return true; }
  toDOM() { const s = document.createElement('span'); s.className = 'ws-ornament'; s.textContent = '✦'; s.setAttribute('aria-label', 'Scene break'); return s; }
}

const deco = {
  hidden: Decoration.replace({}),
  ornament: Decoration.replace({ widget: new OrnamentWidget() }),
  faint: Decoration.mark({ class: 'ws-md-faint' }),
  marker: Decoration.mark({ class: 'ws-md-marker' }),
  italic: Decoration.mark({ class: 'ws-i' }),
  bold: Decoration.mark({ class: 'ws-b' }),
  smallCaps: Decoration.mark({ class: 'ws-sc' }),
  underline: Decoration.mark({ class: 'ws-u' }),
  fnref: Decoration.mark({ class: 'ws-fnref' }),
  listMarker: Decoration.mark({ class: 'ws-list-marker' }),
  indent: Decoration.line({ class: 'ws-indent' }),
  blank: Decoration.line({ class: 'ws-blank' }),
  scene: Decoration.line({ class: 'ws-scene' }),
  quote: Decoration.line({ class: 'ws-quote' }),
  epigraph: Decoration.line({ class: 'ws-quote ws-epigraph' }),
  fndef: Decoration.line({ class: 'ws-fndef' }),
  list: Decoration.line({ class: 'ws-list' }),
  center: Decoration.line({ class: 'ws-align-center' }),
  right: Decoration.line({ class: 'ws-align-right' }),
  justify: Decoration.line({ class: 'ws-align-justify' }),
};
const cached = new Map<string, Decoration>();
function cls(kind: 'mark' | 'line', name: string): Decoration {
  const key = kind + ':' + name;
  let d = cached.get(key);
  if (!d) { d = kind === 'mark' ? Decoration.mark({ class: name }) : Decoration.line({ class: name }); cached.set(key, d); }
  return d;
}

export const stylerStats = { builds: 0, lastBuildMs: 0, maxBuildMs: 0, totalBuildMs: 0, contextScans: 0, linesStyled: 0 };

const SCRIPT_ELEMENTS = new Set<ClassifiedElement>(['note', 'boneyard', 'section', 'synopsis', 'pageBreak']);

/** What the styler reads of a view: its state and the part of the text on screen. */
type StyledView = Pick<EditorView, 'state' | 'viewport'>;

class Styler {
  decorations: DecorationSet = Decoration.none;
  atoms: RangeSet<Decoration> = RangeSet.empty;
  private cache: { pos: number; scanner: LineScanner } | null = null;

  constructor(view: StyledView) { this.build(view); }

  update(u: ViewUpdate) {
    const settings = u.startState.field(settingsField) !== u.state.field(settingsField);
    const pending = u.startState.field(pendingElementField) !== u.state.field(pendingElementField);
    const pieces = u.startState.field(piecesField) !== u.state.field(piecesField);
    if (pieces) this.cache = null;
    if (u.docChanged && this.cache) {
      let above = false;
      const pos = this.cache.pos;
      u.changes.iterChangedRanges((fromA) => { if (fromA < pos) above = true; });
      this.cache = above ? null : { pos: u.changes.mapPos(pos, -1), scanner: this.cache.scanner };
    }
    if (u.docChanged || u.viewportChanged || settings || pending || pieces) this.build(u.view);
  }

  /** The scanner's state at the start of the line at `lineFrom` (a body line). */
  private contextAt(doc: Text, lineFrom: number, bodyFrom: number): LineScanner {
    if (this.cache && this.cache.pos === lineFrom) return this.cache.scanner.clone();
    let pos = bodyFrom;
    let scanner = new LineScanner();
    if (this.cache && this.cache.pos >= bodyFrom && this.cache.pos < lineFrom) { pos = this.cache.pos; scanner = this.cache.scanner.clone(); }
    stylerStats.contextScans++;
    const sink: Span[] = [];
    while (pos < lineFrom) {
      const line = doc.lineAt(pos);
      scanner.line(line.text, line.from, sink);
      sink.length = 0;
      pos = line.to + 1;
    }
    this.cache = { pos: lineFrom, scanner: scanner.clone() };
    return scanner;
  }

  build(view: StyledView) {
    const t0 = performance.now();
    const state = view.state;
    const doc = state.doc;
    const layout = state.field(layoutField);
    const showMarks = state.field(settingsField).showMarks;
    const out: Range<Decoration>[] = [];
    const hiddenRanges: Range<Decoration>[] = [];
    const atomRanges: Range<Decoration>[] = [];
    if (layout.count === 0) { this.decorations = Decoration.none; this.atoms = RangeSet.empty; return; }
    {
      const { from, to } = view.viewport;
      let line = doc.lineAt(from);
      let piece = layout.locate(line.from);
      let scanner: LineScanner | null = null;
      let script: Map<number, ScriptLineStyle> | null = null;
      const settings = state.field(settingsField);
      const format = settings.scriptFormat ?? 'screen';
      const look: ScriptLook = {
        stageUS: format === 'stage-us', musical: settings.musical === true && isStage(format), sitcom: format === 'multicam', radio: format === 'radio',
      };
      for (;;) {
        if (piece + 1 < layout.count && line.from >= layout.start(piece + 1)) { piece++; scanner = null; script = null; }
        const bodyFrom = layout.from[piece], bodyTo = layout.to[piece];
        if (line.from >= bodyFrom && line.from <= bodyTo) {
          if (layout.isScreenplay(piece)) {
            script ??= this.classify(state.doc, bodyFrom, bodyTo, line.from, to, state.field(pendingElementField), look);
            this.styleScript(doc, line.from, line.to, script.get(line.from), look, out);
          } else {
            scanner ??= line.from === bodyFrom ? new LineScanner() : this.contextAt(doc, line.from, bodyFrom);
            const spans: Span[] = [];
            const indent = scanner.line(line.text, line.from, spans);
            stylerStats.linesStyled++;
            if (indent) out.push(deco.indent.range(line.from));
            this.styleProse(spans, line.to < bodyTo, showMarks, out, hiddenRanges, atomRanges, line.text);
            // Space round the whole quotation, not between its lines (as the Swift's `quoteSpacing`).
            if (spans.some((s) => s.kind === 'blockQuote')) {
              if (line.from <= bodyFrom || !doc.line(line.number - 1).text.startsWith('>')) out.push(cls('line', 'ws-quote-first').range(line.from));
              if (line.to >= bodyTo || !doc.line(line.number + 1).text.startsWith('>')) out.push(cls('line', 'ws-quote-last').range(line.from));
            }
          }
        }
        if (line.to >= to || line.number >= doc.lines) break;
        line = doc.line(line.number + 1);
      }
    }
    this.atoms = Decoration.set(hiddenRanges.concat(atomRanges), true);
    this.decorations = Decoration.set(out.concat(hiddenRanges), true);
    const ms = performance.now() - t0;
    stylerStats.builds++;
    stylerStats.lastBuildMs = ms;
    stylerStats.totalBuildMs += ms;
    if (ms > stylerStats.maxBuildMs) stylerStats.maxBuildMs = ms;
  }

  private styleProse(spans: Span[], notLast: boolean, showMarks: boolean, out: Range<Decoration>[], hiddenRanges: Range<Decoration>[], atomRanges: Range<Decoration>[],
    lineText = '') {
    let hiddenFrom = -1, hiddenTo = -1;
    for (const s of spans) {
      switch (s.kind) {
        case 'inlineMarker':
          if (showMarks) out.push(deco.faint.range(s.from, s.to));
          else if (s.from === hiddenTo) hiddenTo = s.to;
          else {
            if (hiddenFrom >= 0) hiddenRanges.push(deco.hidden.range(hiddenFrom, hiddenTo));
            hiddenFrom = s.from; hiddenTo = s.to;
          }
          break;
        case 'marker':
          if (s.to > s.from) out.push(deco.marker.range(s.from, s.to));
          break;
        case 'italic': out.push(deco.italic.range(s.from, s.to)); break;
        case 'bold': out.push(deco.bold.range(s.from, s.to)); break;
        case 'smallCaps': out.push(deco.smallCaps.range(s.from, s.to)); break;
        case 'underline': out.push(deco.underline.range(s.from, s.to)); break;
        case 'highlight': out.push(cls('mark', `ws-hl-${s.ink}`).range(s.from, s.to)); break;
        case 'textColor': out.push(cls('mark', `ws-c-${s.ink}`).range(s.from, s.to)); break;
        case 'footnoteReference': out.push(deco.fnref.range(s.from, s.to)); break;
        // A link's words: the accent colour (unless the writer coloured them) with a fine underline, and its address
        // as a tooltip.
        case 'link': {
          if (s.to <= s.from) break;
          const inked = spans.some((x) => x.kind === 'textColor' && x.from < s.to && s.from < x.to);
          out.push(Decoration.mark({ class: inked ? 'ws-link' : 'ws-link ws-link-ink', attributes: { title: s.address ?? '' } }).range(s.from, s.to));
          break;
        }
        case 'listMarker': out.push(deco.listMarker.range(s.from, s.to)); break;
        // A blank line collapses into the space between paragraphs, except a body's last line (the caret's place
        // after Return), which keeps a line's height as the Swift's terminator does.
        case 'blankLine': if (notLast) out.push(deco.blank.range(s.from)); break;
        case 'sceneBreak':
          out.push(deco.scene.range(s.from));
          if (s.to > s.from) { out.push(deco.ornament.range(s.from, s.to)); atomRanges.push(deco.ornament.range(s.from, s.to)); }
          break;
        case 'blockQuote': out.push(deco.quote.range(s.from)); break;
        case 'epigraph': out.push(deco.epigraph.range(s.from), deco.italic.range(s.from, s.to)); break;
        case 'footnoteDefinition': out.push(deco.fndef.range(s.from)); break;
        case 'listItem': out.push(deco.list.range(s.from)); break;
        case 'aligned': out.push(deco[s.alignment!].range(s.from)); break;
        // A line of verse: ragged, each leading tab a step (tab stops at the steps), a wrapped line hanging under its
        // first word; centred or right-aligned verse has no steps.
        case 'verse': {
          let tabs = 0;
          while (tabs < lineText.length && lineText.charCodeAt(tabs) === 9) tabs++;
          const steps = Math.min(tabs, 6);
          out.push(cls('line', s.alignment ? `ws-verse ws-verse-${s.alignment}` : steps > 0 ? `ws-verse ws-verse-in${steps}` : 'ws-verse').range(s.from));
          break;
        }
      }
    }
    if (hiddenFrom >= 0) hiddenRanges.push(deco.hidden.range(hiddenFrom, hiddenTo));
  }

  /** The screenplay lines around the viewport's part of a body, classified in context, by line start. */
  private classify(doc: Text, bodyFrom: number, bodyTo: number, from: number, to: number, pending: { lineStart: number; element: ClassifiedElement } | null,
    look: ScriptLook) {
    const body = doc.sliceString(bodyFrom, bodyTo);
    const [a, b] = screenplayRange(body, Math.max(0, from - bodyFrom), Math.min(body.length, to - bodyFrom));
    const map = new Map<number, ScriptLineStyle>();
    const lines = classifyRange(body, a, b);
    const styles: ScriptLineStyle[] = lines.map((l) => {
      const start = bodyFrom + l.location;
      const element = pending && pending.lineStart === start ? pending.element : l.element;
      const style: ScriptLineStyle = { element, leading: l.leadingMarker, trailing: l.trailingMarker, content: [start + l.leadingMarker, start + l.length - l.trailingMarker] };
      // A musical's songs: action lines starting `Song:`, centred and bold, never bracketed.
      if (look.musical && element === 'action') {
        const [c, len] = fountainContentRange(l);
        if (isSongLine(body.slice(c, c + len))) style.song = true;
      }
      map.set(start, style);
      return style;
    });
    // A US play's directions (`directionBrackets`): "(" on a run of action lines' first, ")" on its last, unless the
    // writer bracketed it already or it's a "Setting:" or "At rise:" line, which hangs from the margin instead. A
    // musical's song lines are never part of a direction.
    if (look.stageUS) {
      const direction = (k: number) => styles[k].element === 'action' && lines[k].length > 0 && !styles[k].song;
      let n = 0;
      while (n < lines.length) {
        if (!direction(n)) { n++; continue; }
        let end = n;
        while (end + 1 < lines.length && direction(end + 1)) end++;
        const block = lines.slice(n, end + 1).map((l) => { const [c, len] = fountainContentRange(l); return body.slice(c, c + len); })
          .join('\n').replace(/^[ \t]+|[ \t]+$/g, '');
        const bracketed = block.startsWith('(') && block.endsWith(')');
        const labelled = ['setting:', 'at rise:'].some((p) => block.toLowerCase().startsWith(p));
        for (let k = n; k <= end; k++) {
          if (labelled) styles[k].label = true;
          else if (block !== '' && !bracketed) { styles[k].opens = k === n; styles[k].closes = k === end; }
        }
        n = end + 1;
      }
    }
    return map;
  }

  private styleScript(doc: Text, from: number, to: number, line: ScriptLineStyle | undefined, look: ScriptLook, out: Range<Decoration>[]) {
    const element = line?.element ?? 'action';
    const extra = (line?.opens ? ' ws-sp-open' : '') + (line?.closes ? ' ws-sp-close' : '') + (line?.label ? ' ws-sp-label' : '')
      + (line?.song ? ' ws-sp-song' : '');
    out.push(cls('line', `ws-sp ws-sp-${element}${extra}`).range(from));
    if (!line || to === from) return;
    if (line.leading > 0) out.push(deco.marker.range(from, Math.min(to, from + line.leading)));
    if (line.trailing > 0) out.push(deco.marker.range(Math.max(from, to - line.trailing), to));
    if (SCRIPT_ELEMENTS.has(element)) return;
    const [ca, cb] = line.content;
    if (cb <= ca) return;
    const text = doc.sliceString(from, to);
    // A radio play's technical directions and transitions are underlined, as they print (the capitals are the line's
    // class's); a sitcom's entrances and exits are, in its action.
    if (look.radio && (element === 'action' || element === 'transition')) out.push(deco.underline.range(ca, cb));
    if (look.sitcom && element === 'action') {
      ENTRANCES.lastIndex = 0;
      const words = text.slice(ca - from, cb - from);
      for (let m = ENTRANCES.exec(words); m; m = ENTRANCES.exec(words)) out.push(deco.underline.range(ca + m.index, ca + m.index + m[0].length));
    }
    for (const s of fountainInlineSpans(text, ca - from, cb - ca)) {
      const a = from + s.location, b = a + s.length;
      if (b <= a) continue;
      if (s.kind === 'marker' || s.kind === 'note') out.push(deco.marker.range(a, b));
      else if (s.kind === 'italic') out.push(deco.italic.range(a, b));
      else if (s.kind === 'bold') out.push(deco.bold.range(a, b));
      else out.push(deco.underline.range(a, b));
    }
  }
}

/** A script line as the styler sets it: its element, its markers, a US play's brackets or label, a musical's song. */
interface ScriptLineStyle {
  element: ClassifiedElement; leading: number; trailing: number; content: [number, number];
  opens?: boolean; closes?: boolean; label?: boolean; song?: boolean;
}

/** What the project's format changes in how script lines are drawn (the styler's `isMusical`, `isSitcom`, `isRadio`). */
interface ScriptLook { stageUS: boolean; musical: boolean; sitcom: boolean; radio: boolean }

/** "enters", "exits" (any case, whole words): a sitcom underlines them (`EditorStyler.entrances`). */
const ENTRANCES = /(?<![\p{L}\p{N}_])(?:enters|exits|enter|exit)(?![\p{L}\p{N}_])/giu;

/** The decorations the styler would draw for the whole of `state`'s text, as if all of it were on screen (for tests). */
export const decorationsFor = (state: EditorView['state']): DecorationSet =>
  new Styler({ state, viewport: { from: 0, to: state.doc.length } }).decorations;

export const styler = ViewPlugin.fromClass(Styler, {
  decorations: (p) => p.decorations,
  provide: (p) => EditorView.atomicRanges.of((view) => view.plugin(p)?.atoms ?? RangeSet.empty),
});
